import { ThemedText } from "@/components/themed-text";
import { BrandColors } from "@/constants/theme";
import { useAuth } from "@/contexts/auth-context";
import { money } from "@/lib/utils";
import Ionicons from "@react-native-vector-icons/ionicons";
import React from "react";
import {
  ActivityIndicator,
  StyleSheet,
  TouchableOpacity,
  View,
} from "react-native";

interface BookingResponseFooterProps {
  driverEarnings: number;
  loading?: "accept" | "reject" | "quote" | null;
  disabled?: boolean;
  canQuoteHigher?: boolean;
  onQuoteHigher: () => void;
  onDecline: () => void;
  onAccept: () => void;
}

export function BookingResponseFooter({
  driverEarnings,
  loading = null,
  disabled = false,
  canQuoteHigher = true,
  onQuoteHigher,
  onDecline,
  onAccept,
}: BookingResponseFooterProps) {
  const { theme } = useAuth();
  const isLoading = loading !== null;

  return (
    <View
      style={[
        styles.footer,
        { backgroundColor: theme.card, borderColor: theme.border },
      ]}
    >
      <View style={styles.topRowSection}>
        <View style={styles.topRow}>
          <View style={styles.priceSection}>
            <View style={styles.priceIconWrap}>
              <Ionicons
                name="wallet-outline"
                size={16}
                color={BrandColors.darkGray}
              />
            </View>
            <View>
              <ThemedText style={styles.priceLabel} type="extraSmall">
                Etimated Earnings
              </ThemedText>
              <ThemedText style={styles.priceValue} type="defaultSemiBold">
                {money(driverEarnings)}
              </ThemedText>
            </View>
          </View>

          {canQuoteHigher ? (
            <TouchableOpacity
              style={[styles.quoteHigherButton, { backgroundColor: theme.card }]}
              onPress={onQuoteHigher}
              disabled={disabled || isLoading}
              activeOpacity={0.7}
            >
              <Ionicons
                name="create-outline"
                size={16}
                color={BrandColors.primary}
              />
              <ThemedText style={styles.quoteHigherText} type="smallSemiBold">
                Quote Higher
              </ThemedText>
            </TouchableOpacity>
          ) : null}
        </View>
      </View>

      <View style={styles.bottomRow}>
        <TouchableOpacity
          style={styles.declineButton}
          onPress={onDecline}
          disabled={disabled || isLoading}
          activeOpacity={0.7}
        >
          {loading === "reject" ? (
            <ActivityIndicator color={BrandColors.mediumGray} size="small" />
          ) : (
            <>
              <Ionicons name="close" size={18} color={BrandColors.mediumGray} />
              <ThemedText style={styles.declineText} type="smallSemiBold">
                Decline
              </ThemedText>
            </>
          )}
        </TouchableOpacity>

        <TouchableOpacity
          style={styles.acceptButton}
          onPress={onAccept}
          disabled={disabled || isLoading}
          activeOpacity={0.8}
        >
          {loading === "accept" ? (
            <ActivityIndicator color={BrandColors.white} />
          ) : (
            <>
              <Ionicons
                name="notifications-outline"
                size={18}
                color={BrandColors.white}
              />
              <ThemedText style={styles.acceptText} type="smallSemiBold">
                Accept
              </ThemedText>
            </>
          )}
        </TouchableOpacity>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  footer: {
    borderTopWidth: 1,
    borderTopColor: BrandColors.lightGray + "20",
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 8,
    gap: 14,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: -2 },
    shadowOpacity: 0.06,
    shadowRadius: 8,
    elevation: 8,
  },
  topRowSection: {
    gap: 8,
  },
  topRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  priceSection: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    flex: 1,
  },
  priceIconWrap: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: BrandColors.backgroundGray,
    alignItems: "center",
    justifyContent: "center",
  },
  priceLabel: {
    color: BrandColors.mediumGray,
    marginBottom: 2,
  },
  priceValue: {
    fontSize: 24,
    fontWeight: "700",
    lineHeight: 28,
  },
  quoteHigherButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 12,
    borderWidth: 1.5,
    borderColor: BrandColors.primary,
    backgroundColor: BrandColors.white,
  },
  quoteHigherText: {
    fontSize: 14,
    fontWeight: "700",
  },
  bottomRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  declineButton: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    paddingVertical: 14,
    borderWidth: 1.5,
    backgroundColor: "#ffffff",
    borderColor: BrandColors.mediumGray,
    borderRadius: 12,
  },
  declineText: {
    color: BrandColors.mediumGray,
    fontSize: 15,
    fontWeight: "600",
  },
  acceptButton: {
    flex: 2,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    paddingVertical: 16,
    borderRadius: 14,
    backgroundColor: BrandColors.primary,
  },
  acceptText: {
    color: BrandColors.white,
    fontSize: 16,
    fontWeight: "700",
  },
});
